import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import ExplicitBlur from "../components/ExplicitBlur";
import WaitlistForm from "../components/WaitlistForm";

export default function Originals() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/originals").then((r) => setItems(r.data)).finally(() => setLoading(false));
    }, []);

    return (
        <div className="relative z-10 pt-24 sm:pt-32 pb-24" data-testid="originals-page">
            <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
                <div className="mb-16 fade-up max-w-3xl">
                    <div className="label-caps text-[#8C3A3A] mb-4">One of One</div>
                    <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-[#1A1A1A]">
                        Originals<span className="italic text-[#5A5A5A]">.</span>
                    </h1>
                    <p className="mt-8 text-[#5A5A5A] leading-relaxed text-base max-w-2xl">
                        Singular works from the studio — the original charcoals, coloured pencils, and acrylics from
                        which every print is pulled. Each piece is unique, signed, and available once.
                    </p>
                </div>

                {loading ? (
                    <div className="label-caps text-[#5A5A5A]">Loading originals...</div>
                ) : items.length === 0 ? (
                    <div className="py-24 border-t border-black/[0.06]">
                        <div className="font-serif-display text-3xl text-[#1A1A1A] italic mb-4">The next original is on the easel.</div>
                        <p className="text-[#5A5A5A] mb-8">Follow along on Instagram to see it emerge.</p>
                        <Link to="/gallery" className="inline-block border border-black/20 text-[#1A1A1A] px-8 py-3 rounded-full text-sm tracking-wide hover:bg-black/5 transition-colors" data-testid="originals-empty-cta">
                            Browse prints
                        </Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
                        {items.map((p, idx) => (
                            <Link
                                key={p.id}
                                to={`/print/${p.slug}`}
                                className="block group relative"
                                data-testid={`original-${idx}`}
                            >
                                <div className="aspect-[4/5] bg-[#F0EDE5] overflow-hidden mb-6 relative">
                                    <ExplicitBlur
                                        isExplicit={p.is_explicit}
                                        src={fileUrl(p.image_url || p.image_path)}
                                        alt={p.title}
                                        className="w-full h-full transition-transform duration-700 group-hover:scale-105"
                                        testId={`original-reveal-${idx}`}
                                    />
                                    {p.is_sold && (
                                        <div className="absolute inset-0 pointer-events-none flex items-center justify-center" data-testid={`original-sold-overlay-${idx}`}>
                                            <div className="absolute inset-0 bg-white/55 backdrop-blur-[2px] mix-blend-luminosity" />
                                            <div className="relative font-serif-display italic text-white/85 text-6xl sm:text-7xl md:text-8xl tracking-[0.15em] select-none" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.35)" }}>
                                                sold
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="flex justify-between items-baseline">
                                    <div>
                                        <div className="label-caps mb-2">
                                            {p.medium} · Original{p.release_year && ` · ${p.release_year}`}
                                        </div>
                                        <h3 className="font-serif-display text-3xl text-[#1A1A1A] font-light">{p.title}</h3>
                                    </div>
                                    <div className="font-serif-display text-2xl text-[#1A1A1A]">A${p.price.toFixed(0)}</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}

                {!loading && items.length > 0 && (
                    <div className="mt-24 pt-16 border-t border-black/[0.06]" data-testid="originals-waitlist-strip">
                        <div className="max-w-2xl">
                            <div className="label-caps text-[#8C3A3A] mb-3">Studio List</div>
                            <p className="text-[#5A5A5A] leading-relaxed text-sm mb-6">
                                Missed a piece? Join a small list for future drops and rereleases of popular prints. No noise — only when there's something worth sending.
                            </p>
                            <WaitlistForm source="originals_page" cta="Join list" placeholder="Your email" />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
